import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Repeat, ArrowRight } from "lucide-react";

/**
 * Two-lane positioning block — replaces the previous anti-credit messaging
 * with the new dual model: Pay-As-You-Go credit packs OR monthly membership.
 *
 * The verbatim founder lines:
 *   "Just need one project? Pay as you go."
 *   "Creating often? Membership gives you more downloads, voice, and speed."
 */
export default function AntiCreditTrap() {
  return (
    <section
      id="two-lanes"
      data-testid="two-lanes-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-white/10 overflow-hidden"
    >
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/8 blur-[140px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
            Pick your lane
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#F4F4F0] leading-[1.02]">
            Pay as you go. Or go all-in.
          </h2>
          <p className="mt-5 text-[#A0A0A0] text-lg leading-relaxed">
            Whichever fits your workflow, the output is the same business-grade quality.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          <motion.div
            data-testid="lane-payg"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-[#121212] border border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-colors p-7 md:p-9 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">Pay-As-You-Go</p>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-[#F4F4F0] mb-3 leading-tight">
              Just need one project? Pay as you go.
            </h3>
            <p className="text-[#A0A0A0] text-sm md:text-base leading-relaxed mb-6">
              Buy a credit pack from <span className="text-[#F4F4F0] font-semibold">$5 to $100</span>. Generate when you want, walk away when you're done. Credits never expire, failed renders auto-refund.
            </p>
            <Link
              to="/register?intent=payg#pricing"
              data-testid="lane-payg-cta"
              className="mt-auto inline-flex items-center gap-2 self-start text-sm font-bold text-[#D4AF37] hover:text-[#F0C84B]"
            >
              Browse credit packs <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div
            data-testid="lane-membership"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-[#121212] border border-[#D4AF37] pro-card-glow p-7 md:p-9 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-4">
              <Repeat className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">Membership · Premium = BYOK</p>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-[#F4F4F0] mb-3 leading-tight">
              Creating often? Membership unlocks downloads, voice, speed — and Bring-Your-Own-Keys.
            </h3>
            <p className="text-[#A0A0A0] text-sm md:text-base leading-relaxed mb-6">
              From <span className="text-[#F4F4F0] font-semibold">$15/week</span> to{" "}
              <span className="text-[#F4F4F0] font-semibold">$100/month Premium</span>. Premium also connects your own OpenAI / Gemini / Anthropic / ElevenLabs keys — generate on our platform, pay providers directly.
            </p>
            <Link
              to="/register?intent=membership#pricing"
              data-testid="lane-membership-cta"
              className="mt-auto inline-flex items-center gap-2 self-start gold-sheen font-bold px-5 py-3 text-sm transition-all"
            >
              See plans <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
